import {
  useUpdateCompanySettings,
  type CompanySettings,
  useGetCompanySettings,
  useCreateCompanyMutation,
  type CreateCompanyPayload,
  type RegisterForm,
  type CreateCompanyError,
  CreateCompanyErrorCode,
} from "@/api/companies";
import { CONFIRM } from "@/stores/constants";
import type { DeepPartial } from "@/types/utility";
import { computed, reactive, ref } from "vue";
import { useUuid } from "@/composables/uuid";
import { useRoute } from "vue-router";
import useAuthStore from "@/stores/auth";
import { useRootStore } from "@/stores";
import { useI18n } from "vue-i18n";
import useApiStore from "@/stores/api";
import { endpoints } from "@/endpoints";
import type { AxiosError } from "axios";
import { config } from "@/config/envConfig";
import { useHubspot } from "@/api/hubspot";

export type Status<T> = {
  [P in keyof T]: T[P] extends object | undefined
    ? T[P] extends Array<infer U>
      ? U extends object
        ? Array<Status<U>>
        : CONFIRM[]
      : Partial<Status<T[P]>>
    : CONFIRM;
};

type UnknownObject = Record<string, unknown>;

const setNestedSaveStatus = <T extends UnknownObject>(
  saveStatus: T,
  settingKeyPath: string[],
  status: CONFIRM
) => {
  const [settingKey, ...settingKeyPathRemaining] = settingKeyPath;
  if (settingKeyPathRemaining.length === 0) {
    // This is here for catering food images, which require a special case
    if (
      (saveStatus as unknown as CONFIRM) === CONFIRM.SUCCESS &&
      settingKey === "key" &&
      settingKeyPathRemaining.length === 0
    ) {
      return;
    }

    // We're at the end of the setting object key path - time to set the save status
    saveStatus[settingKey as keyof T] = status as T[keyof T];
  } else {
    // The setting object key path continues - take a key out of the chain and repeat
    saveStatus[settingKey as keyof T] ??= {} as T[keyof T];
    setNestedSaveStatus(
      saveStatus[settingKey as keyof T] as UnknownObject,
      settingKeyPathRemaining,
      status
    );
  }
};

export const useCompanySettings = () => {
  const settingsSaveStatus = reactive<Partial<Status<CompanySettings>>>({});

  const updateSaveStatusRecursively = <T extends UnknownObject>(
    setting: T,
    settingKeyPath: string[],
    status: CONFIRM
  ) => {
    Object.entries(setting).forEach(([settingKey, settingValue]) => {
      const newSettingKeyPath = [...settingKeyPath, settingKey];
      if (typeof settingValue === "object" && settingValue !== null) {
        // The setting value we're saving is an object, iterate again to add to the key path
        updateSaveStatusRecursively(
          settingValue as T,
          newSettingKeyPath,
          status
        );
      } else {
        // We've reached the deepest level of the object
        setNestedSaveStatus(settingsSaveStatus, newSettingKeyPath, status);
      }
    });
  };

  const updateCompanySettings = useUpdateCompanySettings(
    (response, { settings }) => {
      Object.entries(settings).forEach(([settingsKey, settingValue]) => {
        updateSaveStatusRecursively(
          { [settingsKey]: settingValue },
          [],
          CONFIRM.SUCCESS
        );
      });
    }
  );

  const getCompanySettings = useGetCompanySettings();
  const settings = computed<Partial<CompanySettings>>(
    () => getCompanySettings.data.value ?? {}
  );

  const updateSetting = async (
    setting: keyof CompanySettings,
    value: DeepPartial<CompanySettings[keyof CompanySettings]>,
    options?: { ignoreLocationGroup?: boolean }
  ) => {
    if (settings.value[setting] === value) return;

    updateSaveStatusRecursively({ [setting]: value }, [], CONFIRM.PENDING);

    await updateCompanySettings.mutateAsync({
      settings: { [setting]: value },
      options,
    });
  };

  const updateSettings = (settings: DeepPartial<CompanySettings>) => {
    updateCompanySettings.mutate({ settings });

    updateSaveStatusRecursively(settings, [], CONFIRM.PENDING);
  };

  return { updateSetting, settingsSaveStatus, updateSettings };
};

export const useCreateCompany = () => {
  const { generateUuid } = useUuid();

  const { query } = useRoute();

  const { removeCookies } = useAuthStore();

  const { locale } = useI18n();

  const apiStore = useApiStore();

  const rootStore = useRootStore();

  const loading = ref(false);

  const { t, te } = useI18n();

  const errors = reactive({
    emailConflict: false,
    marketplaceTokenInvalid: false,
    marketplaceTokenUnknown: false,
    phoneInvalid: false,
    forbidden: false,
  });

  const handleCreateCompanyError = (
    error: AxiosError<CreateCompanyError> & { handledGlobally: boolean }
  ) => {
    loading.value = false;

    if (!error || !error.response) return;

    const errorCode = error.response?.data?.error.errorCode;

    switch (errorCode) {
      case CreateCompanyErrorCode.EMAIL_INVALID:
        errors.emailConflict = true;
        break;
      case CreateCompanyErrorCode.MARKET_PLACE_TOKEN_INVALID:
        errors.marketplaceTokenInvalid = true;
        break;
      case CreateCompanyErrorCode.MARKET_PLACE_TOKEN_UNKNOWN:
        errors.marketplaceTokenUnknown = true;
        break;
      case CreateCompanyErrorCode.PHONE_INVALID:
        errors.phoneInvalid = true;
        break;
      default:
        if (
          error.response?.data?.error?.message ===
          CreateCompanyErrorCode.FORBIDDEN
        ) {
          errors.forbidden = true;
        }
        if (!error.handledGlobally) {
          rootStore.handleException({ error });
        }
        return true;
    }
  };

  const { mutateAsync: createCompanyMutate } = useCreateCompanyMutation(
    undefined,
    handleCreateCompanyError
  );

  const createCompany = async (createCompanyForm: RegisterForm) => {
    try {
      loading.value = true;
      const { submitCompany } = useHubspot();

      const createCompanyPk = generateUuid();

      const url = createCompanyForm.regionEndpoint
        ? `${createCompanyForm.regionEndpoint}${endpoints.company.create(
            createCompanyPk
          )}`
        : apiStore.getEndpoint(
            "rest",
            endpoints.company.create(createCompanyPk)
          );

      const payload: CreateCompanyPayload = {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        firstName: createCompanyForm.firstName,
        lastName: createCompanyForm.lastName,
        companyName: createCompanyForm.company,
        email: createCompanyForm.email,
        phone: createCompanyForm.phone,
        locale: locale.value,
        recaptchaResult: createCompanyForm.recaptchaResult,
      };

      if (query.marketplaceToken)
        payload.marketplaceToken = query.marketplaceToken as string;

      removeCookies();

      await createCompanyMutate({ payload, url });

      if (window.growsumo) {
        window.growsumo.data.name = `${createCompanyForm.firstName} ${createCompanyForm.lastName}`;
        window.growsumo.data.email = createCompanyForm.email;
        window.growsumo.data.customer_key = createCompanyPk;
        window.growsumo.createSignup();
      }

      await submitCompany(createCompanyForm);

      const hubspotFormCompletionUrl = te("urls.registerThankYou")
        ? t("urls.registerThankYou")
        : config.HUBSPOT_FORM_COMPLETION_URL ??
          "https://www.swipedon.com/register-thank-you";

      window.location.href = hubspotFormCompletionUrl;
    } catch (error) {
      return;
    }
  };

  return { createCompany, errors, loading };
};
